// src/contexts/TrackChangesRevertNoticeContext.tsx

import * as React from "react";

/**
 * Lightweight global state for the Track Changes revert notice.
 *
 * Producer: AutoRedlinePreview.handleApplyAll, which detects mid-flight Track
 * Changes toggles and reverts the document state before applying edits.
 *
 * Consumer: AppContent, which renders the notice as a top-of-taskpane
 * InfoBanner — mirrors the same placement used by the addin-open
 * useTrackChangesAutoEnable banner so the two share a consistent UX.
 *
 * The banner lives outside the suggested-changes card to avoid visual conflict
 * with the card's success/applied background, and to give the user a single
 * predictable place to look for Track Changes notifications.
 */
interface TrackChangesRevertNotice {
  message: string;
}

interface TrackChangesRevertNoticeContextValue {
  notice: TrackChangesRevertNotice | null;
  showNotice: (message: string) => void;
  dismissNotice: () => void;
}

const TrackChangesRevertNoticeContext = React.createContext<
  TrackChangesRevertNoticeContextValue | undefined
>(undefined);

export const TrackChangesRevertNoticeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [notice, setNotice] = React.useState<TrackChangesRevertNotice | null>(null);

  const showNotice = React.useCallback((message: string) => {
    setNotice({ message });
  }, []);

  const dismissNotice = React.useCallback(() => {
    setNotice(null);
  }, []);

  const value = React.useMemo(
    () => ({ notice, showNotice, dismissNotice }),
    [notice, showNotice, dismissNotice]
  );

  return (
    <TrackChangesRevertNoticeContext.Provider value={value}>
      {children}
    </TrackChangesRevertNoticeContext.Provider>
  );
};

/**
 * Access the revert-notice state. Returns no-op handlers when used outside a
 * provider, so components (and unit tests) can render in isolation without
 * needing to set up the provider.
 */
export const useTrackChangesRevertNotice = (): TrackChangesRevertNoticeContextValue => {
  const context = React.useContext(TrackChangesRevertNoticeContext);
  if (!context) {
    return {
      notice: null,
      showNotice: () => {},
      dismissNotice: () => {},
    };
  }
  return context;
};
